import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { auditFindingColumns } from 'constants/auditFindingsTableColumn';
import CustomTable from '../CustomTable';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import Helpers from 'utils/Helpers';

const GapsAuditFindingsTable: React.FC = () => {
  const { lang, orgContext, assignedAuditFindingsUpdated } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      orgContext: state.global.hierarchicalLevelsScope,
      assignedAuditFindingsUpdated: state.gaps.assignedAuditFindingsUpdated
    })
  );

  const [auditData, setAuditData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(2);

  const filterParams = React.useMemo(
    () => Helpers.toApiFilters(orgContext),
    [orgContext]
  );

  useEffect(() => {
    if (!lang || Object.keys(filterParams).length === 0) return;

    (async () => {
      try {
        setLoading(true);
        const { module, moduleId, ...filtersWithoutModule } = filterParams;
        const audits = await gapsOp.fetchAudits(lang, filtersWithoutModule);
        setAuditData(
          audits?.map((audit: any) => ({
            key: audit.afg_id,
            id: audit.afg_id.toString(),
            text: audit.afg_desc_en || `Audit Finding ${audit.afg_id}`
          })) || []
        );
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    })();
  }, [lang, filterParams, assignedAuditFindingsUpdated]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return auditData.slice(start, start + pageSize);
  }, [currentPage, pageSize, auditData]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return (
    <CustomTable
      drawTable
      columns={auditFindingColumns(
        pageSize,
        paginatedData.length,
        paginatedData
      )}
      dataSource={paginatedData}
      itemsCount={auditData.length}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: auditData.length,
        onChange: handlePaginationChange
      }}
    />
  );
};

export default GapsAuditFindingsTable;
